import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, FileText, Plus, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { eur, fmtDate, invId, isDomTom, normalizeList } from "@/lib/invoice";
import { StatusBadge } from "@/components/StatusBadge";

const TABS = [
  { id: "all", label: "Toutes" },
  { id: "issued", label: "Émises" },
  { id: "received", label: "Reçues" },
  { id: "domtom", label: "Octroi de mer" },
];

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/invoices")
      .then(({ data }) => setInvoices(normalizeList(data)))
      .catch(() => toast.error("Impossible de charger les factures"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = invoices;
    if (tab === "issued") list = list.filter((i) => (i.direction || "issued") !== "received");
    if (tab === "received") list = list.filter((i) => i.direction === "received");
    if (tab === "domtom") list = list.filter(isDomTom);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((i) =>
        [i.invoice_number, i.buyer?.name, i.seller?.name]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q)),
      );
    }
    return list;
  }, [invoices, tab, query]);

  const exportAll = async () => {
    setExporting(true);
    try {
      const res = await api.get("/invoices/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "facturxpress-export.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export téléchargé");
    } catch {
      toast.error("Export impossible");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="invoices-page" className="max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline-label mb-2">Archives</p>
          <h1 className="font-display font-light tracking-tighter text-3xl md:text-4xl">Factures</h1>
        </div>
        <div className="flex gap-3">
          <button
            onClick={exportAll}
            disabled={exporting}
            data-testid="export-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-sm hover:border-[#00D4FF] hover:-translate-y-0.5 transition-[transform,border-color] duration-200 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            Exporter
          </button>
          <Link
            to="/app/factures/nouvelle"
            data-testid="invoices-create-cta"
            className="inline-flex items-center gap-2 rounded-full border border-[#2563EB]/50 text-[#7FA6FF] px-5 py-2.5 text-sm font-semibold hover:bg-[#2563EB]/15 hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
          >
            <Plus className="h-4 w-4" />
            Nouvelle facture
          </Link>
          <Link
            to="/app/importer"
            data-testid="invoices-import-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold hover:bg-[#00D4FF] hover:text-[#050505] hover:-translate-y-0.5 transition-[transform,background-color,color] duration-200"
          >
            <Upload className="h-4 w-4" />
            Importer
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex rounded-full border border-white/10 bg-[#0A0E17] p-1" data-testid="invoice-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors duration-200 ${
                tab === t.id ? "bg-[#2563EB] text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="invoice-search"
            placeholder="Numéro, client, fournisseur…"
            className="w-full rounded-full bg-black/40 border border-white/10 pl-11 pr-4 py-2.5 text-sm placeholder:text-zinc-600 focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] outline-none transition-[border-color,box-shadow] duration-200"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-24" data-testid="invoices-loading">
          <div className="h-10 w-10 rounded-full border-2 border-[#2563EB] border-t-transparent animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 py-20 text-center" data-testid="invoices-empty">
          <FileText className="h-8 w-8 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">Aucune facture dans cette vue.</p>
          <Link to="/app/importer" className="mt-2 inline-block text-sm text-[#00D4FF] hover:underline">
            Importer un PDF
          </Link>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-[#0A0E17] overflow-hidden" data-testid="invoices-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  <th className="px-5 py-4 overline-label">Numéro</th>
                  <th className="px-5 py-4 overline-label">Date</th>
                  <th className="px-5 py-4 overline-label hidden md:table-cell">Tiers</th>
                  <th className="px-5 py-4 overline-label text-right">TTC</th>
                  <th className="px-5 py-4 overline-label text-right hidden lg:table-cell">Octroi</th>
                  <th className="px-5 py-4 overline-label">Statut</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((inv, i) => {
                  const id = invId(inv);
                  const tier = inv.direction === "received" ? inv.seller?.name : inv.buyer?.name;
                  return (
                    <motion.tr
                      key={id || i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.5) }}
                      data-testid={`invoice-row-${id}`}
                      className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors duration-150"
                    >
                      <td className="px-5 py-4">
                        <Link to={`/app/factures/${id}`} data-testid={`invoice-link-${id}`} className="font-mono text-[#00D4FF] hover:underline">
                          {inv.invoice_number || id}
                        </Link>
                      </td>
                      <td className="px-5 py-4 text-zinc-400">{fmtDate(inv.issue_date || inv.created_at)}</td>
                      <td className="px-5 py-4 hidden md:table-cell text-zinc-300 max-w-[200px] truncate">{tier || "—"}</td>
                      <td className="px-5 py-4 text-right font-mono">{eur(inv.total_ttc)}</td>
                      <td className="px-5 py-4 text-right font-mono hidden lg:table-cell">
                        {isDomTom(inv) ? <span className="text-[#00D4FF]">{eur(inv.total_octroi)}</span> : <span className="text-zinc-600">—</span>}
                      </td>
                      <td className="px-5 py-4"><StatusBadge invoice={inv} /></td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
